import { Link } from "react-router-dom";
import { ArrowUpRight, TrendingUp, ShoppingBag, Briefcase, Plane } from "lucide-react";
import { Section, Eyebrow, FadeIn } from "@/components/Section";
import useSEO from "@/hooks/useSEO";

const cases = [
  {
    id: "lumen-skincare",
    industry: "DTC · Skincare",
    icon: ShoppingBag,
    title: "From €40k to €380k MRR in 9 months",
    summary:
      "Rebuilt a Polish DTC skincare brand's Meta funnel, launched Performance Max for category capture, and re-architected GA4 for true blended ROAS reporting.",
    metrics: [
      { k: "9.5×", v: "ROAS" },
      { k: "−42%", v: "CAC" },
      { k: "€380k", v: "MRR" },
    ],
    services: ["Meta Ads", "Google Ads", "Analytics"],
    span: "md:col-span-8",
  },
  {
    id: "kerno-saas",
    industry: "B2B SaaS",
    icon: Briefcase,
    title: "+218% qualified pipeline in 6 months",
    summary:
      "Took a Series A SaaS from pure outbound to a paid + SEO engine driving 60% of demo bookings. New programmatic SEO hub indexed in 6 weeks.",
    metrics: [
      { k: "+218%", v: "MQLs" },
      { k: "11k", v: "organic / mo" },
      { k: "€68", v: "CPL" },
    ],
    services: ["Google Ads", "SEO"],
    span: "md:col-span-4",
  },
  {
    id: "altura-travel",
    industry: "Travel · Marketplace",
    icon: Plane,
    title: "Scaling a marketplace to 14 EU countries",
    summary:
      "Localised paid social and search across 14 markets with native creative pipelines. Built a content engine ranking for >4,200 long-tail destinations.",
    metrics: [
      { k: "14", v: "markets" },
      { k: "4.2k", v: "keywords #1-3" },
      { k: "3.8×", v: "blended ROAS" },
    ],
    services: ["Meta Ads", "SEO", "Creative"],
    span: "md:col-span-5",
  },
  {
    id: "voltic-ecom",
    industry: "Ecommerce · Electronics",
    icon: TrendingUp,
    title: "PMax overhaul → +63% revenue YoY",
    summary:
      "Restructured a fragmented Google Ads account into intent-segmented PMax + brand search. Shopping feed re-engineered for margin protection.",
    metrics: [
      { k: "+63%", v: "revenue" },
      { k: "5.1×", v: "ROAS" },
      { k: "−28%", v: "cost / order" },
    ],
    services: ["Google Ads", "Analytics"],
    span: "md:col-span-7",
  },
];

export default function CaseStudies() {
  useSEO({
    title: "Case Studies · Performance marketing results",
    description:
      "Real growth case studies: DTC skincare scaled to €380k MRR, B2B SaaS +218% pipeline, marketplace across 14 EU markets, ecommerce +63% YoY.",
    path: "/case-studies",
  });
  return (
    <div data-testid="page-case-studies">
      <Section className="pt-36">
        <FadeIn>
          <Eyebrow>Case studies</Eyebrow>
        </FadeIn>
        <FadeIn delay={0.05}>
          <h1 className="font-display mt-5 text-5xl sm:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.92] max-w-5xl">
            Results we'd
            <br />
            <span className="text-[#ccff00]">put our name on.</span>
          </h1>
        </FadeIn>
        <FadeIn delay={0.1}>
          <p className="mt-8 text-zinc-400 text-lg leading-relaxed max-w-2xl">
            A selection of growth engagements across DTC, SaaS, ecommerce and
            marketplaces. Every number below is blended, attributed and ready
            for due-diligence.
          </p>
        </FadeIn>
      </Section>

      <Section className="border-t border-white/10 bg-[#0a0a0b]" dataTestid="case-grid">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {cases.map((c, i) => (
            <FadeIn key={c.id} delay={i * 0.06} className={c.span}>
              <article
                data-testid={`case-${c.id}`}
                className="group h-full border border-white/10 bg-[#0f0f10] hover:border-[#ccff00] p-8 md:p-10 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_80px_-30px_rgba(204,255,0,0.35)]"
              >
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <c.icon
                      className="h-5 w-5 text-[#ccff00]"
                      strokeWidth={1.5}
                    />
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      {c.industry}
                    </span>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">
                    0{i + 1}
                  </span>
                </div>

                <h2 className="font-display text-2xl sm:text-4xl font-black tracking-tighter leading-[1.02]">
                  {c.title}
                </h2>
                <p className="mt-4 text-zinc-400 text-sm leading-relaxed">
                  {c.summary}
                </p>

                <div className="mt-8 grid grid-cols-3 border-t border-white/10 pt-6">
                  {c.metrics.map((m) => (
                    <div key={m.v}>
                      <div className="font-display text-2xl sm:text-3xl font-black text-[#ccff00]">
                        {m.k}
                      </div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                        {m.v}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  {c.services.map((s) => (
                    <span
                      key={s}
                      className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-300 border border-white/10 px-2.5 py-1"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </article>
            </FadeIn>
          ))}
        </div>
      </Section>

      <Section className="border-t border-white/10">
        <FadeIn>
          <div className="border border-white/10 bg-[#0f0f10] p-10 md:p-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <Eyebrow>Your turn</Eyebrow>
              <h2 className="font-display mt-4 text-3xl sm:text-5xl font-black tracking-tighter leading-[0.95]">
                Want to be the next
                <br />
                <span className="text-[#ccff00]">case study?</span>
              </h2>
            </div>
            <div className="md:col-span-4 md:justify-self-end">
              <Link
                to="/contact"
                data-testid="case-final-cta"
                className="inline-flex items-center gap-2 bg-[#ccff00] hover:bg-[#d4ff33] text-black px-6 py-4 font-bold tracking-tight transition group"
              >
                Start a project
                <ArrowUpRight className="h-5 w-5 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
        </FadeIn>
      </Section>
    </div>
  );
}
